import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import { Link } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React from 'react';
import Button from '../../atoms/button';
import RicohLogo from '../../atoms/ricoh-logo/ricoh-logo';
import * as Styles from './footer.module.scss';

interface Props {
  className?: string;
}

const Footer: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();

  return (
    <div
      className={`bg-black/[.03] text-xs text-lsGray-700 ${Styles.footer} ${className}`}
    >
      <div className='mx-4 max-w-screen-lg py-10 lg:mx-auto lg:px-11 lg:pb-8 lg:pt-16'>
        <div className='mb-8 space-y-8 font-bold lg:mb-6 lg:flex lg:justify-between lg:space-y-0'>
          <div className='flex items-end justify-between lg:flex-col lg:items-start lg:justify-normal'>
            <Link className='lg:mb-6' to='/'>
              <RicohLogo color='#383838' className='mb-2 w-16 lg:w-24' />
              <h5 className='text-base font-bold lg:text-lg'>
                4K Live Streaming
              </h5>
            </Link>
            <Button
              className='h-8 px-6 text-xs lg:w-40'
              label={t('footer.login')}
              onClick={() => {
                open(
                  'https://console.livestreaming.mw.smart-integration.ricoh.com/',
                  '_blank',
                  'noopener noreferrer',
                );
              }}
            />
          </div>
          <div className='space-y-4'>
            <p className='font-roboto text-sm font-medium text-lsGray-400'>
              {t('footer.getting_started')}
            </p>
            <ul className='flex space-x-4 lg:flex-col lg:space-x-0 lg:space-y-4'>
              <li>
                <Link to='/docs/service'>{t('footer.service')}</Link>
              </li>
              <li>
                <Link to='/docs/guide'>{t('footer.guide')}</Link>
              </li>
              <li>
                <Link to='/docs/price'>{t('footer.price')}</Link>
              </li>
            </ul>
          </div>
          <div className='space-y-4'>
            <p className='font-roboto text-sm font-medium text-lsGray-400'>
              {t('footer.developer')}
            </p>
            <ul className='flex space-x-4 lg:flex-col lg:space-x-0 lg:space-y-4'>
              <li>
                <Link to='/docs/application-developer-guide'>
                  {t('footer.development')}
                </Link>
              </li>
              <li>
                <Link to='/docs/sdk-support-period'>{t('footer.support')}</Link>
              </li>
              <li>
                <Link to='/docs/error-without-h264codec'>
                  {t('footer.faq')}
                </Link>
              </li>
              <li>
                <a
                  href='https://mw.smart-integration.status.ricoh.com/'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {t('footer.operating_status')}
                  <ArrowTopRightOnSquareIcon
                    className='ml-1 inline h-4 w-4 align-text-bottom'
                    aria-hidden='true'
                  />
                </a>
              </li>
            </ul>
          </div>
          <div className='space-y-4'>
            <p className='font-roboto text-sm font-medium text-lsGray-400'>
              {t('footer.terms_policy')}
            </p>
            <ul className='flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-4'>
              <li>
                <a
                  href='https://jp.ricoh.com/terms_of_use'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {t('footer.terms_of_use')}
                  <ArrowTopRightOnSquareIcon
                    className='ml-1 inline h-4 w-4 align-text-bottom'
                    aria-hidden='true'
                  />
                </a>
              </li>
              <li>
                <a
                  href='/docs/privacy-policy/'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {t('footer.privacy_policy')}
                  <ArrowTopRightOnSquareIcon
                    className='ml-1 inline h-4 w-4 align-text-bottom'
                    aria-hidden='true'
                  />
                </a>
              </li>
              <li>
                <a
                  href='https://livestreamingricohhelp.zendesk.com/hc/jp/requests/new'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {t('footer.contact')}
                  <ArrowTopRightOnSquareIcon
                    className='ml-1 inline h-4 w-4 align-text-bottom'
                    aria-hidden='true'
                  />
                </a>
              </li>
            </ul>
          </div>
        </div>
        <p className='text-center text-lsGray-300'>
          ©︎RICOH All Rights Reserved.
        </p>
      </div>
    </div>
  );
};

Footer.displayName = 'Footer';

export default Footer;
